"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type {
  ApprovalStatus,
  CommunityProfile,
  FeatureGrant,
  User,
} from "../_types/content";

const SESSION_STORAGE_KEY = "rg-mock-session";
const DEMO_USER_ID = "user-jonathan-bethony";

interface PendingConnection {
  targetAccountId: string;
  message?: string;
}

interface SessionOverlay {
  loggedInUserId?: string;
  approvalStatuses: Record<string, ApprovalStatus>;
  featureGrants: Record<string, FeatureGrant[]>;
  profileOverrides: Record<string, CommunityProfile>;
  pendingConnections: PendingConnection[];
}

interface MockSessionValue {
  users: SessionUser[];
  loggedInMember: SessionUser | null;
  isSessionReady: boolean;
  loginMockUser: () => void;
  logout: () => void;
  updateApprovalStatus: (userId: string, state: ApprovalStatus) => void;
  updateFeatureGrants: (userId: string, grants: FeatureGrant[]) => void;
  convertToCommunityProfile: (
    userId: string,
    profile: CommunityProfile,
  ) => void;
  hasPendingConnection: (targetAccountId: string) => boolean;
  requestConnection: (targetAccountId: string, message?: string) => void;
}

const initialOverlay: SessionOverlay = {
  approvalStatuses: {},
  featureGrants: {},
  profileOverrides: {},
  pendingConnections: [],
};

const MockSessionContext = createContext<MockSessionValue | undefined>(
  undefined,
);

type SessionUser = User & { managedProfileIds: string[] };

function loadOverlay(): SessionOverlay {
  try {
    const value = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (!value) return initialOverlay;
    return { ...initialOverlay, ...(JSON.parse(value) as SessionOverlay) };
  } catch {
    return initialOverlay;
  }
}

export function MockSessionProvider({
  userSeeds,
  children,
}: {
  userSeeds: User[];
  children: React.ReactNode;
}) {
  const [overlay, setOverlay] = useState<SessionOverlay>(initialOverlay);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    const hydrationTask = window.setTimeout(() => {
      setOverlay(loadOverlay());
      setIsHydrated(true);
    }, 0);

    return () => window.clearTimeout(hydrationTask);
  }, []);

  useEffect(() => {
    if (isHydrated) {
      window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(overlay));
    }
  }, [isHydrated, overlay]);

  const users = useMemo(
    () =>
      userSeeds.map((user) => {
        const common = {
          ...user,
          ...(user.accountType === "community"
            ? {
                approvalStatus:
                  overlay.approvalStatuses[user.id] ?? user.approvalStatus,
                featureGrants:
                  overlay.featureGrants[user.id] ?? user.featureGrants,
              }
            : {}),
          managedProfileIds:
            user.accountType === "community-profile" ? [user.profile.slug] : [],
        };
        const profile = overlay.profileOverrides[user.id];
        return profile ? { ...common, kind: "community-profile" as const, profile } : common;
      }),
    [overlay, userSeeds],
  );

  const loggedInMember =
    users.find((user) => user.id === overlay.loggedInUserId) ?? null;

  const updateUserOverlay = useCallback(
    (key: "approvalStatuses" | "featureGrants" | "profileOverrides", userId: string, value: ApprovalStatus | FeatureGrant[] | CommunityProfile) => {
      setOverlay((current) => ({
        ...current,
        [key]: { ...current[key], [userId]: value },
      }));
    },
    [],
  );

  const value = useMemo<MockSessionValue>(
    () => ({
      users,
      loggedInMember,
      isSessionReady: isHydrated,
      loginMockUser: () =>
        setOverlay((current) => ({
          ...current,
          loggedInUserId:
            userSeeds.find((user) => user.id === DEMO_USER_ID)?.id ??
            userSeeds[0]?.id,
        })),
      logout: () =>
        setOverlay((current) => ({
          ...current,
          loggedInUserId: undefined,
        })),
      updateApprovalStatus: (userId, state) =>
        updateUserOverlay("approvalStatuses", userId, state),
      updateFeatureGrants: (userId, grants) =>
        updateUserOverlay("featureGrants", userId, grants),
      convertToCommunityProfile: (userId, profile) =>
        updateUserOverlay("profileOverrides", userId, profile),
      hasPendingConnection: (targetAccountId) =>
        overlay.pendingConnections.some(
          (connection) => connection.targetAccountId === targetAccountId,
        ),
      requestConnection: (targetAccountId, message) =>
        setOverlay((current) => ({
          ...current,
          pendingConnections: [
            ...current.pendingConnections.filter(
              (connection) => connection.targetAccountId !== targetAccountId,
            ),
            { targetAccountId, message },
          ],
        })),
    }),
    [
      isHydrated,
      loggedInMember,
      overlay.pendingConnections,
      updateUserOverlay,
      userSeeds,
      users,
    ],
  );

  return (
    <MockSessionContext.Provider value={value}>
      {children}
    </MockSessionContext.Provider>
  );
}

export function useMockSession() {
  const context = useContext(MockSessionContext);
  if (!context) {
    throw new Error("useMockSession must be used within MockSessionProvider");
  }
  return context;
}
